import { z } from 'zod'
import { MIN_TRANSLATED_SHARE } from '../config'
import { PageElement } from './elements'
import { Localized } from './localized'
import { RichTextDoc, richTextToPlainText } from './richText'
import { WebsiteDocument, documentSchema } from './document'
import { isLocalAsset } from '../assets/localAssets'

/**
 * The rules that only apply when the owner presses Publish.
 *
 * They are deliberately not part of `documentSchema`. A draft has to be
 * storable while it is still wrong, or the editor loses work every time someone
 * adds a photo and walks away before writing its alt text. The brief says the
 * same thing about `alt` in §6, and the split generalises: save is permissive,
 * publish is strict.
 */

const hasText = (value: string | undefined) =>
  !!value && value.trim().length > 0

const hasRichText = (doc: RichTextDoc | undefined) =>
  !!doc && richTextToPlainText(doc).length > 0

/** One translatable field, reduced to "is it filled in language X". */
type Field = (language: string) => boolean

function fieldsOfElement(element: PageElement): Field[] {
  switch (element.type) {
    case 'heading':
      return [language => hasText(element.text[language])]
    case 'text':
      return [
        language =>
          hasRichText((element.content as Localized<RichTextDoc>)[language])
      ]
    case 'image':
      return element.caption
        ? [
            language => hasText(element.alt[language]),
            language => hasText(element.caption?.[language])
          ]
        : [language => hasText(element.alt[language])]
    case 'button':
      return [language => hasText(element.label[language])]
    case 'reservation':
      // The consent line is prose a guest has to be able to read; the button
      // text has a default in every language the renderer knows, so it counts
      // only once the owner has written one.
      return [
        ...(element.consent ? [(language: string) => hasText(element.consent?.[language])] : []),
        ...(element.label ? [(language: string) => hasText(element.label?.[language])] : [])
      ]
    default:
      // Everything else carries no prose of its own: a gallery is asset ids, a
      // menu block is category ids, a map is an address.
      return []
  }
}

function fieldsOfDocument(doc: WebsiteDocument): Field[] {
  const fields: Field[] = []

  doc.pages.forEach(page => {
    fields.push(language => hasText(page.title[language]))
    fields.push(language => hasText(page.seo.title[language]))
    fields.push(language => hasText(page.seo.description[language]))

    page.blocks.forEach(row =>
      row.columns.forEach(column =>
        column.items.forEach(item => fields.push(...fieldsOfElement(item)))
      )
    )
  })

  return fields
}

export interface TranslationProgress {
  /** Fields that carry content in the default language. */
  total: number
  /** How many of those also have content in the language asked about. */
  filled: number
  /** `filled / total`, or 1 when there is nothing to translate. */
  share: number
}

/**
 * How far one language version has got.
 *
 * Only fields filled in the default language count towards `total`. A field
 * nobody wrote in Polish is not an untranslated field, it is an empty one, and
 * counting it would make an otherwise complete English page look unfinished.
 *
 * The editor shows this as a progress bar; publication uses it as a gate.
 */
export function translationProgress(
  doc: WebsiteDocument,
  language: string
): TranslationProgress {
  const fields = fieldsOfDocument(doc).filter(field =>
    field(doc.defaultLanguage)
  )
  const filled = fields.filter(field => field(language)).length
  return {
    total: fields.length,
    filled,
    share: fields.length === 0 ? 1 : filled / fields.length
  }
}

/**
 * The document as it has to look to go live.
 *
 * Beyond the draft rules: something has to be published, every photo needs alt
 * text, every published page needs a search title, and a language version has
 * to be genuinely translated before it gets its own URL.
 *
 * That last one is not pedantry. PLAN.md §2.2: thousands of venues share one
 * domain, so a half-translated page is a duplicate-content signal charged to
 * every customer at once, not just to the venue that published it.
 */
/** Every photo reference a document holds, wherever it is kept. */
export function assetRefsOf(doc: WebsiteDocument): string[] {
  const refs: string[] = []

  doc.pages.forEach(page => {
    if (page.seo.ogImageAssetId) refs.push(page.seo.ogImageAssetId)
    page.blocks.forEach(row => {
      if (row.background.type === 'image') refs.push(row.background.assetId)
      row.columns.forEach(column =>
        column.items.forEach(item => {
          if (item.type === 'image') refs.push(item.assetId)
          if (item.type === 'gallery') refs.push(...item.assetIds)
        })
      )
    })
  })

  return refs
}

/**
 * Whether the page leans on a photo only this browser can see.
 *
 * `menu_core_be` has no websites domain, so a picked photo is held in the
 * owner's own browser and referenced as `local:`. That is fine for editing and
 * impossible for publishing: nobody else can fetch it.
 */
export function hasLocalAssets(doc: WebsiteDocument): boolean {
  return assetRefsOf(doc).some(isLocalAsset)
}

/**
 * Why a page cannot go live. The rules raise these codes rather than
 * sentences: a zod schema has no `t()`, and a message baked in here was English
 * in a Polish panel, shown after a path like `pages.0.blocks.1`. The panel
 * names each under `WEBSITE.PUBLISH_ERROR.*`.
 */
export const PUBLISH_ISSUES = [
  'no-published-page',
  'missing-seo-title',
  'missing-alt',
  'local-photo',
  'untranslated'
] as const
export type PublishIssueCode = (typeof PUBLISH_ISSUES)[number]

export interface PublishIssue {
  /** `invalid` for anything the document schema itself refused. */
  code: PublishIssueCode | 'invalid'
  params?: Record<string, string | number>
}

export const toPublishIssue = (issue: z.ZodIssue): PublishIssue => {
  const known = (PUBLISH_ISSUES as readonly string[]).includes(issue.message)
  if (!known) return { code: 'invalid' }
  return {
    code: issue.message as PublishIssueCode,
    params: issue.code === z.ZodIssueCode.custom ? issue.params : undefined
  }
}

export const publishableDocumentSchema = documentSchema.superRefine(
  (doc, ctx) => {
    const published = doc.pages.filter(page => page.status === 'published')
    if (published.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pages'],
        message: 'no-published-page'
      })
    }

    doc.pages.forEach((page, pageIndex) => {
      if (page.status !== 'published') return

      if (!hasText(page.seo.title[doc.defaultLanguage])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pages', pageIndex, 'seo', 'title'],
          message: 'missing-seo-title'
        })
      }

      page.blocks.forEach((row, rowIndex) =>
        row.columns.forEach((column, columnIndex) =>
          column.items.forEach((item, itemIndex) => {
            if (item.type !== 'image') return
            if (hasText(item.alt[doc.defaultLanguage])) return
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [
                'pages',
                pageIndex,
                'blocks',
                rowIndex,
                'columns',
                columnIndex,
                'items',
                itemIndex,
                'alt'
              ],
              message: 'missing-alt'
            })
          })
        )
      )
    })

    /*
      A photo nobody but this browser can fetch. The editor is happy to draw it
      and a visitor would get a broken image, so it is refused here rather than
      discovered by whoever opens the published page.
    */
    if (hasLocalAssets(doc)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pages'],
        message: 'local-photo'
      })
    }

    doc.languages
      .filter(language => language !== doc.defaultLanguage)
      .forEach(language => {
        const progress = translationProgress(doc, language)
        if (progress.share < MIN_TRANSLATED_SHARE) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['languages'],
            message: 'untranslated',
            params: {
              language,
              share: Math.round(progress.share * 100),
              needed: Math.round(MIN_TRANSLATED_SHARE * 100)
            }
          })
        }
      })
  }
)
